'use client';

// Archiving a Guest Child, and the household's former members.
//
// To a family, "archive" is deleting someone from the household: they stop
// showing. Underneath nothing is deleted. They move to the holding household
// with a Former Household link back, which is what "Archived" lists and
// "Bring back" undoes. Only a Guest Child can be archived from here; removing
// anyone else is its own design (see the note atop event-details AddPerson).
//
// Former members stay quiet on purpose: a small link under the faces that only
// appears when there is someone to show, and a sheet when it is asked for.

import { SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { useEffect, useState } from 'react';
import { Archive, Loader2, RotateCcw } from 'lucide-react';
import type { FormerMember, Person } from '@/lib/types';
import { fmtShortDate } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';
import { BTN_QUIET, CARD, PageBody, Pii, ProfileSheet, useConfirm } from './ui';

export const GUEST_CHILD_POSITION_ID = 5;

/** Quiet "Archive" under a Guest Child's name. */
export function ArchiveLink({
  person,
  onArchived,
}: {
  person: Person;
  /** Select someone else and refresh former members. */
  onArchived: () => void;
}) {
  const { api, data, update, run } = useProfile();
  const confirm = useConfirm();
  const first = person.nickname ?? person.firstName;

  const archive = async () => {
    const ok = await confirm({
      title: `Archive ${first}?`,
      body: `${first} will no longer show with your family. You can bring them back from Archived.`,
      confirmLabel: 'Archive',
    });
    if (!ok) return;
    const before = data.members;
    onArchived();
    update((d) => ({ ...d, members: d.members.filter((m) => m.contactId !== person.contactId) }));
    await run(
      () => api.archiveMember(person.contactId),
      () => update((d) => ({ ...d, members: before })),
      `${first} archived`
    );
  };

  return (
    <button
      type="button"
      onClick={() => void archive()}
      className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#1c2b39] hover:underline"
    >
      <Archive className="h-3 w-3" />
      Archive
    </button>
  );
}

/**
 * "Archived · 2" under the faces, and its sheet. Renders nothing until
 * it knows there is somebody to show. `version` re-reads after an archive.
 */
export function FormerMembers({ version }: { version: number }) {
  const { api, data, update, run } = useProfile();
  // Kept on the shared profile data: the Family card lists them under Elsewhere too.
  // The link and sheet are for archived people only; anyone who moved into a
  // household of their own shows under Elsewhere as "lived here" instead.
  const former = (data.formerMembers ?? []).filter((f) => f.canBringBack);
  const setFormer = (fn: (list: FormerMember[]) => FormerMember[]) =>
    update((d) => ({ ...d, formerMembers: fn(d.formerMembers ?? []) }));
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getFormerMembers(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setFormer(() => list);
      })
      // An extra, never the page: on failure it simply does not appear.
      .catch(() => {});
    return () => controller.abort();
    // Re-read when a fresh /me replaced the data and dropped the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, version, data.formerMembers === undefined]);

  if (former.length === 0) return null;

  const bringBack = async (f: FormerMember) => {
    setBusyId(f.contactId);
    const ok = await run(
      () => api.bringBackMember(f.contactId),
      undefined,
      `${f.firstName} is back`
    );
    if (ok) {
      setFormer((list) => list.filter((x) => x.contactId !== f.contactId));
      try {
        const fresh = await api.getMe();
        update((d) => ({ ...fresh, formerMembers: d.formerMembers }));
      } catch {
        // The bring-back saved; the face appears on the next load.
      }
    }
    setBusyId(null);
  };

  return (
    <>
      <div className="-mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] font-semibold tracking-[0.12em] text-gray-400 uppercase underline-offset-4 transition-colors hover:text-[#1c2b39] hover:underline"
        >
          Archived · {former.length}
        </button>
      </div>

      <ProfileSheet open={open} onClose={() => setOpen(false)} title="Archived">
        <SheetPage name="main">
          <PageBody>
            <ul className="flex flex-col gap-2">
              {former.map((f) => (
                <li key={f.contactId} className={`${CARD} flex items-center gap-3 px-4 py-3`}>
                  <Avatar name={f.name} imageUrl={f.imageUrl} size={44} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-[#1c2b39]">
                      <Pii>{f.name}</Pii>
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {[
                        f.position,
                        `left ${fmtShortDate(f.leftOn)}`,
                        f.nowHouseholdName ? `now with the ${f.nowHouseholdName} household` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {f.canBringBack ? (
                    <button
                      type="button"
                      disabled={busyId !== null}
                      onClick={() => void bringBack(f)}
                      className={`${BTN_QUIET} shrink-0`}
                    >
                      {busyId === f.contactId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                      )}
                      Bring back
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </PageBody>
        </SheetPage>
      </ProfileSheet>
    </>
  );
}
